/**
 * Politique 401 partagée : un 401 n’est pas toujours une session Rintintin morte
 * (Google Calendar expiré, OAuth, probes auth).
 */

const NON_SESSION_401_URL_NEEDLES = [
  '/auth/google/',
  '/calendars/',
  '/calendar/',
  '/feature-flags',
  '/get-profile',
  '/get-pro',
  '/get-profiles',
  '/create-service-availability',
  '/respond-reschedule',
] as const;

const WEB_GRACE_MS = 7_200_000;
const COUNT_WINDOW_MS = 60_000;
const LOGOUT_AFTER_COUNT = 5;

let authErrorCount = 0;
let lastAuthErrorTime = 0;

export function urlLooksLikeNonSession401(url?: string): boolean {
  if (!url) return false;
  const u = url.toLowerCase();
  return NON_SESSION_401_URL_NEEDLES.some((needle) => u.includes(needle));
}

export function isOnAuthPath(pathname?: string): boolean {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return p.includes('/login') || p.includes('/auth/') || p.includes('/signup');
}

type AxiosLike401 = {
  config?: { url?: string };
  response?: {
    status?: number;
    data?: {
      error_code?: string;
      error?: string;
      message?: string;
      Message?: string;
    };
  };
};

/** 401 session JWT expirée (API decorators.error_code TOKEN_EXPIRED). */
export function isRintintinJwtExpired401(error: AxiosLike401): boolean {
  if (error.response?.status !== 401) return false;
  const data = error.response?.data;
  if (!data || typeof data !== 'object') return false;
  if (data.error_code === 'TOKEN_EXPIRED') return true;
  const msg = `${data.Message ?? ''} ${data.message ?? ''} ${data.error ?? ''}`;
  return /expir/i.test(msg);
}

/**
 * True uniquement si on doit invalider la session Rintintin (logout + login).
 * Aligne apiService sur l’intercepteur Axios d’App : pas de logout au 1er 401.
 */
export function shouldInvalidateSessionOn401(
  error: AxiosLike401,
  now: number = Date.now()
): boolean {
  if (error.response?.status !== 401) return false;
  if (urlLooksLikeNonSession401(error.config?.url)) return false;
  if (isOnAuthPath()) return false;
  if (isRintintinJwtExpired401(error)) return true;

  let timeSinceLogin = Infinity;
  try {
    const lastLoginTime = localStorage.getItem('lastLoginTime');
    timeSinceLogin = lastLoginTime ? now - parseInt(lastLoginTime, 10) : Infinity;
  } catch {
    /* ignore */
  }
  if (timeSinceLogin < WEB_GRACE_MS) return false;

  if (now - lastAuthErrorTime > COUNT_WINDOW_MS) {
    authErrorCount = 0;
  }
  authErrorCount += 1;
  lastAuthErrorTime = now;
  return authErrorCount >= LOGOUT_AFTER_COUNT;
}

/** Tests unitaires uniquement. */
export function resetSession401GuardForTests(): void {
  authErrorCount = 0;
  lastAuthErrorTime = 0;
}
